import React, {useCallback, useEffect, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import lscache from "lscache";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {gap} from "@pg-design/helpers-css";
import {mb, pr} from "@pg-design/helpers-css-strings";
import {Loader} from "@pg-design/loader-module";
import {FormProviderRHF, InputField} from "@pg-design/rhf-components-module";
import {Highlight, Text} from "@pg-design/text-module";
import {gtmEventPhoneVerification} from "@pg-mono/google-tag-manager/src/gtm_event_phone_verification";
import {useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {phoneNumberFormat, phoneNumberFormatNational} from "@pg-mono/string-utils";

import {PHONE_VERIFICATION_CODE_SENT_LS_KEY, PHONE_VERIFICATION_TIMER_START_LS_KEY} from "../../constants/phone_verification_constants";
import {useUserPhoneVerificationContext} from "../context/UserPhoneVerificationContext";
import {digitFields, IFormData, useDigitInputNavigation} from "../hooks/use_digit_input_navigation";
import {useWebOtp} from "../hooks/use_web_otp";
import {clearPhoneVerificationTimerLS} from "../utils/clear_phone_verification_timer_ls";
import {getPhoneVerificationCodeRemainingTime} from "../utils/get_phone_verification_code_remaining_time";
import {getTimeInMinutes} from "../utils/get_time_in_minutes";

const HTTP_STATUS_FOR_TOO_MANY_REQUESTS = 429;

export interface IUserPhoneVerificationFormProps {
    isOpen: boolean;
    onClose?: () => void;
    onSuccess?: () => void;
    onChangeNumber: () => void;
    phoneNumber?: string;
    storageKeyNamespace?: string;
    allowResendAfterTooManyRequests?: boolean;
    // Set when each open of this modal is a brand-new verification attempt (e.g. the promotion flows mount a fresh
    // modal per submission). Drops any cooldown left in localStorage by a previous attempt so the send re-fires and
    // the resend countdown restarts, instead of being suppressed by a stale "code already sent" flag.
    restartCooldownOnOpen?: boolean;
}

const digitValidation = Yup.number()
    .transform((value, originalValue) => {
        if (originalValue === "" || isNaN(originalValue)) {
            return null;
        }
        return Number(originalValue);
    })
    .required("Niepoprawna wartość")
    .min(0, "Niepoprawna wartość")
    .max(9, "Niepoprawna wartość");

const validationSchema = Yup.object<IFormData>().shape({
    firstDigit: digitValidation.nullable(),
    secondDigit: digitValidation.nullable(),
    thirdDigit: digitValidation.nullable(),
    fourthDigit: digitValidation.nullable()
});

export const UserPhoneVerificationForm = (props: IUserPhoneVerificationFormProps) => {
    const {
        isOpen,
        onClose,
        onSuccess,
        onChangeNumber,
        phoneNumber: propPhoneNumber,
        storageKeyNamespace,
        allowResendAfterTooManyRequests = false,
        restartCooldownOnOpen = false
    } = props;
    const context = useUserPhoneVerificationContext();
    const [formError, setFormError] = useState<{message: string; status: number} | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);
    const [userPhoneNumber, setUserPhoneNumber] = useState<string | null>(propPhoneNumber || null);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);
    const digitNavigation = useDigitInputNavigation(inputRefs);
    const prevIsOpenRef = useRef<boolean>(false);
    const timerStorageKey = storageKeyNamespace ? `${storageKeyNamespace}:${PHONE_VERIFICATION_TIMER_START_LS_KEY}` : PHONE_VERIFICATION_TIMER_START_LS_KEY;
    const codeSentStorageKey = storageKeyNamespace ? `${storageKeyNamespace}:${PHONE_VERIFICATION_CODE_SENT_LS_KEY}` : PHONE_VERIFICATION_CODE_SENT_LS_KEY;

    const [timerStartTime, setTimerStartTime] = useState<number | null>(() => {
        // A new verification attempt must not inherit a cooldown left by a previous one, so drop the persisted state
        // and let the auto-send-on-open below re-fire the code and start a fresh timer (otherwise a stale "code
        // already sent" flag suppresses the send and the resend countdown never starts).
        if (restartCooldownOnOpen) {
            lscache.remove(timerStorageKey);
            lscache.remove(codeSentStorageKey);
            return null;
        }

        const savedStartTime = lscache.get(timerStorageKey);
        return savedStartTime ? parseInt(savedStartTime, 10) : null;
    });

    const [timer, setTimer] = useState(() => getPhoneVerificationCodeRemainingTime(timerStartTime));
    const formattedTime = getTimeInMinutes(timer);
    const hotlinePhoneFormat = context.hotlinePhoneNumber ? phoneNumberFormat(context.hotlinePhoneNumber) : "";

    // Fetch user profile to get phone number if not provided
    useEffect(() => {
        if (isOpen && !propPhoneNumber) {
            context.getUserProfile().then((profile) => {
                if (profile?.phone) {
                    setUserPhoneNumber(profile.phone);
                }
            });
        }
    }, [isOpen, propPhoneNumber, context]);

    useEffect(() => {
        if (timerStartTime) {
            const interval = setInterval(() => {
                const remaining = getPhoneVerificationCodeRemainingTime(timerStartTime);
                setTimer(remaining);

                if (remaining === 0) {
                    lscache.remove(timerStorageKey);
                    clearInterval(interval);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [timerStartTime, timerStorageKey]);

    useEffect(() => {
        if (isOpen && (formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS || allowResendAfterTooManyRequests)) {
            const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);
            const codeAlreadySent = Boolean(lscache.get(codeSentStorageKey));

            // Only send GTM event when modal transitions from closed to open
            if (!prevIsOpenRef.current) {
                gtmEventPhoneVerification("view");
            }

            prevIsOpenRef.current = isOpen;

            // First SMS in a flow: auto-send once. Later opens / new modals: only „Wyślij kod” (sendNewCode sets the LS flag).
            const shouldAutoSendOnOpen = !codeAlreadySent && (currentRemainingTime === 0 || !timerStartTime);

            if (shouldAutoSendOnOpen) {
                handlers.setNewStartTime();
                sendNewCode();
            } else if (currentRemainingTime > 0) {
                setTimer(currentRemainingTime);
            } else {
                setTimer(0);
            }
        } else {
            prevIsOpenRef.current = isOpen;
        }
    }, [allowResendAfterTooManyRequests, codeSentStorageKey, isOpen, formError]);

    const form = useFormRHF<IFormData>({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
        defaultValues: {
            firstDigit: null,
            secondDigit: null,
            thirdDigit: null,
            fourthDigit: null
        }
    });

    const handleWebOtpCode = (code: string) => {
        const digits = code.replace(/[^0-9]/g, "").slice(0, 4);

        digits.split("").forEach((digit, index) => {
            if (index < digitFields.length) {
                form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
            }
        });

        if (digits.length > 0) {
            const lastIndex = Math.min(digits.length, 4) - 1;
            digitNavigation.focusFieldByIndex(lastIndex);
        }
    };

    useWebOtp({
        enabled: isOpen,
        onCodeReceived: handleWebOtpCode
    });

    const handlers = {
        onCloseButton: () => {
            if (onClose) {
                onClose();
            }
        },
        changeNumberButton: () => {
            // Don't let the user navigate away mid-submission while the verification/lead work is running.
            if (isSubmittingRef.current) {
                return;
            }
            if (storageKeyNamespace) {
                handlers.clearVerificationStorage();
            } else {
                handlers.clearStartTime();
            }
            onChangeNumber();
        },
        clearErrorMessage: () => {
            setFormError((prevState) => (prevState ? {...prevState, message: ""} : null));
        },
        setNewStartTime: () => {
            const startTime = Date.now();
            setTimerStartTime(startTime);
            lscache.set(timerStorageKey, startTime.toString());
        },
        clearStartTime: () => {
            setTimerStartTime(null);
            lscache.remove(timerStorageKey);
        },
        clearVerificationStorage: () => {
            setTimerStartTime(null);
            setTimer(0);
            lscache.remove(timerStorageKey);
            lscache.remove(codeSentStorageKey);
        },
        inputFocusChange: (fieldName: keyof IFormData, value: string) => {
            const numericValue = value.replace(/[^0-9]/g, "");
            const startIndex = digitFields.indexOf(fieldName);

            // Handle multi-digit input (e.g. iOS Chrome SMS autofill)
            if (numericValue.length > 1) {
                const digits = numericValue.slice(0, 4);

                digits.split("").forEach((digit, i) => {
                    const targetIndex = startIndex + i;
                    if (targetIndex < digitFields.length) {
                        form.setValue(digitFields[targetIndex], parseInt(digit), {shouldValidate: true});
                    }
                });

                const lastFilledIndex = Math.min(startIndex + digits.length, digitFields.length) - 1;
                digitNavigation.focusFieldByIndex(lastFilledIndex);
                return;
            }

            // Clearing this field must also clear following fields, otherwise RHF keeps stale digits
            if (!numericValue) {
                for (let i = startIndex; i < digitFields.length; i++) {
                    form.setValue(digitFields[i], null);
                }
                return;
            }

            form.setValue(fieldName, parseInt(numericValue.slice(-1)));

            digitNavigation.moveToNextField(fieldName);
        },
        inputKeyDown: (fieldName: keyof IFormData, event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Backspace") {
                const currentValue = (event.target as HTMLInputElement).value;

                // If current field is empty, move to previous field
                if (!currentValue) {
                    digitNavigation.moveToPreviousField(fieldName);
                }
            }
        },
        inputPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
            event.preventDefault();
            const pastedText = event.clipboardData.getData("text");
            const digits = pastedText.replace(/[^0-9]/g, "").slice(0, 4);

            if (digits.length === 0) {
                return;
            }

            digits.split("").forEach((digit, index) => {
                if (index < digitFields.length) {
                    form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
                }
            });

            const nextEmptyIndex = digits.length < 4 ? digits.length : 3;
            digitNavigation.focusFieldByIndex(nextEmptyIndex);
        }
    };

    const sendNewCode = async () => {
        // Block resends while a verification submit is in flight so the modal can't fire competing requests.
        if (isSubmittingRef.current) {
            return null;
        }

        const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);

        if (currentRemainingTime > 0) {
            return null;
        }

        handlers.setNewStartTime();
        handlers.clearErrorMessage();
        setIsLoading(true);

        try {
            const response = await context.sendPhoneVerificationCode();

            if (response.error) {
                const error = response.error;

                if (error.status === HTTP_STATUS_FOR_TOO_MANY_REQUESTS && !allowResendAfterTooManyRequests) {
                    // Clear timer and localStorage for 429 error status
                    handlers.clearStartTime();
                    setTimer(0);
                }

                setFormError(error);
                if (context.onError) {
                    context.onError(error);
                }
            } else {
                lscache.set(codeSentStorageKey, "1");
            }
        } catch (_err) {
            const error = {message: "Wystąpił błąd podczas wysyłania kodu", status: 500};
            setFormError(error);
            if (context.onError) {
                context.onError(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = useCallback(
        async (values: IFormData) => {
            // The auto-submit effect re-fires whenever all four digits are filled; bail out if a submit is
            // already in flight so the verification + onSuccess work can't be triggered twice.
            if (isSubmittingRef.current) {
                return;
            }

            const phoneVerificationCode = `${values.firstDigit}${values.secondDigit}${values.thirdDigit}${values.fourthDigit}`;

            setFormError((prevState) => (prevState ? {...prevState, message: ""} : null));
            isSubmittingRef.current = true;
            setIsSubmitting(true);

            try {
                const response = await context.verifyPhone(phoneVerificationCode);

                if (response.error) {
                    setFormError(response.error);
                    if (context.onError) {
                        context.onError(response.error);
                    }
                    return;
                }

                gtmEventPhoneVerification("submitted");
                if (storageKeyNamespace) {
                    lscache.remove(timerStorageKey);
                    lscache.remove(codeSentStorageKey);
                } else {
                    clearPhoneVerificationTimerLS();
                }

                // Awaited so the CTA stays in its loading state through the work onSuccess starts (typically the
                // Turnstile-protected lead submission). Firing it without awaiting would drop the loader while the
                // captcha/submission is still running, leaving a clickable button on the modal.
                await onSuccess?.();
            } catch (_err) {
                const error = {message: "Wystąpił błąd podczas weryfikacji", status: 500};
                setFormError(error);
                if (context.onError) {
                    context.onError(error);
                }
            } finally {
                isSubmittingRef.current = false;
                setIsSubmitting(false);
            }
        },
        [codeSentStorageKey, context, onSuccess, storageKeyNamespace, timerStorageKey]
    );

    useEffect(() => {
        const subscription = form.watch((values) => {
            const {firstDigit, secondDigit, thirdDigit, fourthDigit} = values;
            const digits = [firstDigit, secondDigit, thirdDigit, fourthDigit];

            if (digits.every((digit) => digit != null)) {
                form.handleSubmit(onSubmit)();
            }
        });

        return () => subscription.unsubscribe();
    }, [form, onSubmit]);

    if (!userPhoneNumber) {
        return null;
    }

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <Text
                variant="headline_3"
                align="left"
                mb={1}
                css={css`
                    ${pr(1)}
                `}
            >
                Zweryfikuj swój <Highlight>numer telefonu</Highlight>
            </Text>

            <Text variant="body_copy_2" mb={4} color="var(--colors-gray-800)">
                Wpisz 4 cyfrowy kod aktywacyjny, który wysłaliśmy na Twój numer telefonu{" "}
                <span css={boldTextStyle}>{phoneNumberFormatNational(userPhoneNumber)}</span>.{" "}
                <span css={changeNumberButtonStyle} onClick={handlers.changeNumberButton}>
                    Zmień numer
                </span>
            </Text>

            <FormProviderRHF {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div css={formStyle}>
                        {digitFields.map((fieldName, index) => (
                            <InputField
                                key={fieldName}
                                ref={(el) => {
                                    inputRefs.current[index] = el;
                                }}
                                name={fieldName}
                                css={inputStyle}
                                inputMode="numeric"
                                autoComplete={index === 0 ? "one-time-code" : "off"}
                                onChange={(e) => {
                                    handlers.inputFocusChange(fieldName, e.target.value);
                                    const numeric = e.target.value.replace(/[^0-9]/g, "");
                                    if (numeric.length > 1) {
                                        e.target.value = numeric.slice(-1);
                                    }
                                }}
                                onKeyDown={(e) => handlers.inputKeyDown(fieldName, e)}
                                onPaste={handlers.inputPaste}
                            />
                        ))}
                    </div>
                    {formError && (
                        <Text variant="info_txt_1" css={errorMessageStyle}>
                            {formError.message}
                        </Text>
                    )}
                    <Button variant="filled_primary" type="submit" fullWidth css={buttonStyle} isLoading={isSubmitting} disabled={isSubmitting}>
                        POTWIERDŹ
                    </Button>
                    {formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS || allowResendAfterTooManyRequests ? (
                        <Text variant="info_txt_1" as="div" css={timer > 0 ? timerStyle : sendCodeInfoStyle} onClick={sendNewCode}>
                            {isLoading ? <Loader size="sm" /> : timer > 0 ? `Wyślij kod ponownie za: ${formattedTime}` : "Wyślij kod ponownie"}
                        </Text>
                    ) : (
                        <Text variant="info_txt_1" css={timerStyle}>
                            Nie otrzymałeś kodu? <br />
                            {context.hotlinePhoneNumber && (
                                <>
                                    Skontaktuj się z naszym biurem obsługi:{" "}
                                    <a
                                        css={[
                                            timerStyle,
                                            css`
                                                font-weight: 700;
                                            `
                                        ]}
                                        href={`tel:+${hotlinePhoneFormat}`}
                                    >
                                        {phoneNumberFormat(context.hotlinePhoneNumber)}
                                    </a>
                                </>
                            )}
                        </Text>
                    )}
                </form>
            </FormProviderRHF>
        </>
    );
};

const formStyle = (theme: Theme) => css`
    display: flex;
    flex-direction: row;
    ${gap(1)};
    margin: auto;
    ${mb(4)};
    height: 72px;
    width: fit-content;
    justify-content: center;

    @media (min-width: ${theme.breakpoints.sm}) {
        ${mb(3)};
        height: 112px;
    }
`;

const buttonStyle = css`
    align-items: center;
    ${mb(2)};
`;

const boldTextStyle = css`
    font-weight: 700;
`;

const changeNumberButtonStyle = css`
    text-decoration: underline;
    cursor: pointer;
`;

const inputStyle = (theme: Theme) => css`
    text-align: center;
    border-radius: 8px;
    border: 1px solid var(--colors-gray-600);
    font-size: 30.79px;

    @media (min-width: ${theme.breakpoints.sm}) {
        font-size: 35.92px;
    }

    & div {
        border: none;
    }

    & > div:first-of-type {
        height: 72px;
        width: fit-content;
        max-width: 72px;

        @media (min-width: ${theme.breakpoints.sm}) {
            height: 112px;
            max-width: 112px;
        }

        & > div:first-of-type {
            height: 70px;
            width: fit-content;
            max-width: 72px;

            @media (min-width: ${theme.breakpoints.sm}) {
                height: 110px;
                max-width: 112px;
            }
        }
    }

    & input {
        height: 100%;
        width: 100%;
        text-align: center;
        font-weight: 600;
    }

    & [data-name="field-message"] {
        display: flex;
        justify-content: center;
    }
`;

const timerStyle = css`
    color: var(--colors-gray-800);
    text-align: center;
`;

const sendCodeInfoStyle = css`
    color: var(--colors-gray-800);
    text-align: center;
    cursor: pointer;
    text-decoration: underline;
`;

const errorMessageStyle = css`
    color: var(--colors-danger);
    text-align: center;
    ${mb(2)};
`;
import React, {useCallback, useEffect, useRef, useState} from "react";
import {css, Theme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import lscache from "lscache";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {gap} from "@pg-design/helpers-css";
import {mb, pr} from "@pg-design/helpers-css-strings";
import {Loader} from "@pg-design/loader-module";
import {FormProviderRHF, InputField} from "@pg-design/rhf-components-module";
import {Highlight, Text} from "@pg-design/text-module";
import {gtmEventPhoneVerification} from "@pg-mono/google-tag-manager/src/gtm_event_phone_verification";
import {useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {phoneNumberFormat, phoneNumberFormatNational} from "@pg-mono/string-utils";

import {PHONE_VERIFICATION_CODE_SENT_LS_KEY, PHONE_VERIFICATION_TIMER_START_LS_KEY} from "../../constants/phone_verification_constants";
import {useUserPhoneVerificationContext} from "../context/UserPhoneVerificationContext";
import {digitFields, IFormData, useDigitInputNavigation} from "../hooks/use_digit_input_navigation";
import {useWebOtp} from "../hooks/use_web_otp";
import {clearPhoneVerificationTimerLS} from "../utils/clear_phone_verification_timer_ls";
import {getPhoneVerificationCodeRemainingTime} from "../utils/get_phone_verification_code_remaining_time";
import {getTimeInMinutes} from "../utils/get_time_in_minutes";

const HTTP_STATUS_FOR_TOO_MANY_REQUESTS = 429;

export interface IUserPhoneVerificationFormProps {
    isOpen: boolean;
    onClose?: () => void;
    onSuccess?: () => void;
    onChangeNumber: () => void;
    phoneNumber?: string;
    storageKeyNamespace?: string;
    allowResendAfterTooManyRequests?: boolean;
    // Set when each open of this modal is a brand-new verification attempt (e.g. the promotion flows mount a fresh
    // modal per submission). Drops any cooldown left in localStorage by a previous attempt so the send re-fires and
    // the resend countdown restarts, instead of being suppressed by a stale "code already sent" flag.
    restartCooldownOnOpen?: boolean;
}

const digitValidation = Yup.number()
    .transform((value, originalValue) => {
        if (originalValue === "" || isNaN(originalValue)) {
            return null;
        }
        return Number(originalValue);
    })
    .required("Niepoprawna wartość")
    .min(0, "Niepoprawna wartość")
    .max(9, "Niepoprawna wartość");

const validationSchema = Yup.object<IFormData>().shape({
    firstDigit: digitValidation.nullable(),
    secondDigit: digitValidation.nullable(),
    thirdDigit: digitValidation.nullable(),
    fourthDigit: digitValidation.nullable()
});

export const UserPhoneVerificationForm = (props: IUserPhoneVerificationFormProps) => {
    const {
        isOpen,
        onClose,
        onSuccess,
        onChangeNumber,
        phoneNumber: propPhoneNumber,
        storageKeyNamespace,
        allowResendAfterTooManyRequests = false,
        restartCooldownOnOpen = false
    } = props;
    const context = useUserPhoneVerificationContext();
    const [formError, setFormError] = useState<{message: string; status: number} | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);
    const [userPhoneNumber, setUserPhoneNumber] = useState<string | null>(propPhoneNumber || null);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);
    const digitNavigation = useDigitInputNavigation(inputRefs);
    const prevIsOpenRef = useRef<boolean>(false);
    const timerStorageKey = storageKeyNamespace ? `${storageKeyNamespace}:${PHONE_VERIFICATION_TIMER_START_LS_KEY}` : PHONE_VERIFICATION_TIMER_START_LS_KEY;
    const codeSentStorageKey = storageKeyNamespace ? `${storageKeyNamespace}:${PHONE_VERIFICATION_CODE_SENT_LS_KEY}` : PHONE_VERIFICATION_CODE_SENT_LS_KEY;

    const [timerStartTime, setTimerStartTime] = useState<number | null>(() => {
        // A new verification attempt must not inherit a cooldown left by a previous one, so drop the persisted state
        // and let the auto-send-on-open below re-fire the code and start a fresh timer (otherwise a stale "code
        // already sent" flag suppresses the send and the resend countdown never starts).
        if (restartCooldownOnOpen) {
            lscache.remove(timerStorageKey);
            lscache.remove(codeSentStorageKey);
            return null;
        }

        const savedStartTime = lscache.get(timerStorageKey);
        return savedStartTime ? parseInt(savedStartTime, 10) : null;
    });

    const [timer, setTimer] = useState(() => getPhoneVerificationCodeRemainingTime(timerStartTime));
    const formattedTime = getTimeInMinutes(timer);
    const hotlinePhoneFormat = context.hotlinePhoneNumber ? phoneNumberFormat(context.hotlinePhoneNumber) : "";

    // Fetch user profile to get phone number if not provided
    useEffect(() => {
        if (isOpen && !propPhoneNumber) {
            context.getUserProfile().then((profile) => {
                if (profile?.phone) {
                    setUserPhoneNumber(profile.phone);
                }
            });
        }
    }, [isOpen, propPhoneNumber, context]);

    useEffect(() => {
        if (timerStartTime) {
            const interval = setInterval(() => {
                const remaining = getPhoneVerificationCodeRemainingTime(timerStartTime);
                setTimer(remaining);

                if (remaining === 0) {
                    lscache.remove(timerStorageKey);
                    clearInterval(interval);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [timerStartTime, timerStorageKey]);

    useEffect(() => {
        if (isOpen && (formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS || allowResendAfterTooManyRequests)) {
            const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);
            const codeAlreadySent = Boolean(lscache.get(codeSentStorageKey));

            // Only send GTM event when modal transitions from closed to open
            if (!prevIsOpenRef.current) {
                gtmEventPhoneVerification("view");
            }

            prevIsOpenRef.current = isOpen;

            // First SMS in a flow: auto-send once. Later opens / new modals: only „Wyślij kod” (sendNewCode sets the LS flag).
            const shouldAutoSendOnOpen = !codeAlreadySent && (currentRemainingTime === 0 || !timerStartTime);

            if (shouldAutoSendOnOpen) {
                handlers.setNewStartTime();
                sendNewCode();
            } else if (currentRemainingTime > 0) {
                setTimer(currentRemainingTime);
            } else {
                setTimer(0);
            }
        } else {
            prevIsOpenRef.current = isOpen;
        }
    }, [allowResendAfterTooManyRequests, codeSentStorageKey, isOpen, formError]);

    const form = useFormRHF<IFormData>({
        resolver: yupResolver(validationSchema),
        mode: "onBlur",
        defaultValues: {
            firstDigit: null,
            secondDigit: null,
            thirdDigit: null,
            fourthDigit: null
        }
    });

    const handleWebOtpCode = (code: string) => {
        const digits = code.replace(/[^0-9]/g, "").slice(0, 4);

        digits.split("").forEach((digit, index) => {
            if (index < digitFields.length) {
                form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
            }
        });

        if (digits.length > 0) {
            const lastIndex = Math.min(digits.length, 4) - 1;
            digitNavigation.focusFieldByIndex(lastIndex);
        }
    };

    useWebOtp({
        enabled: isOpen,
        onCodeReceived: handleWebOtpCode
    });

    const handlers = {
        onCloseButton: () => {
            if (onClose) {
                onClose();
            }
        },
        changeNumberButton: () => {
            // Don't let the user navigate away mid-submission while the verification/lead work is running.
            if (isSubmittingRef.current) {
                return;
            }
            if (storageKeyNamespace) {
                handlers.clearVerificationStorage();
            } else {
                handlers.clearStartTime();
            }
            onChangeNumber();
        },
        clearErrorMessage: () => {
            setFormError((prevState) => (prevState ? {...prevState, message: ""} : null));
        },
        setNewStartTime: () => {
            const startTime = Date.now();
            setTimerStartTime(startTime);
            lscache.set(timerStorageKey, startTime.toString());
        },
        clearStartTime: () => {
            setTimerStartTime(null);
            lscache.remove(timerStorageKey);
        },
        clearVerificationStorage: () => {
            setTimerStartTime(null);
            setTimer(0);
            lscache.remove(timerStorageKey);
            lscache.remove(codeSentStorageKey);
        },
        inputFocusChange: (fieldName: keyof IFormData, value: string) => {
            const numericValue = value.replace(/[^0-9]/g, "");
            const startIndex = digitFields.indexOf(fieldName);

            // Handle multi-digit input (e.g. iOS Chrome SMS autofill)
            if (numericValue.length > 1) {
                const digits = numericValue.slice(0, 4);

                digits.split("").forEach((digit, i) => {
                    const targetIndex = startIndex + i;
                    if (targetIndex < digitFields.length) {
                        form.setValue(digitFields[targetIndex], parseInt(digit), {shouldValidate: true});
                    }
                });

                const lastFilledIndex = Math.min(startIndex + digits.length, digitFields.length) - 1;
                digitNavigation.focusFieldByIndex(lastFilledIndex);
                return;
            }

            // Clearing this field must also clear following fields, otherwise RHF keeps stale digits
            if (!numericValue) {
                for (let i = startIndex; i < digitFields.length; i++) {
                    form.setValue(digitFields[i], null);
                }
                return;
            }

            form.setValue(fieldName, parseInt(numericValue.slice(-1)));

            digitNavigation.moveToNextField(fieldName);
        },
        inputKeyDown: (fieldName: keyof IFormData, event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Backspace") {
                const currentValue = (event.target as HTMLInputElement).value;

                // If current field is empty, move to previous field
                if (!currentValue) {
                    digitNavigation.moveToPreviousField(fieldName);
                }
            }
        },
        inputPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
            event.preventDefault();
            const pastedText = event.clipboardData.getData("text");
            const digits = pastedText.replace(/[^0-9]/g, "").slice(0, 4);

            if (digits.length === 0) {
                return;
            }

            digits.split("").forEach((digit, index) => {
                if (index < digitFields.length) {
                    form.setValue(digitFields[index], parseInt(digit), {shouldValidate: true});
                }
            });

            const nextEmptyIndex = digits.length < 4 ? digits.length : 3;
            digitNavigation.focusFieldByIndex(nextEmptyIndex);
        }
    };

    const sendNewCode = async () => {
        // Block resends while a verification submit is in flight so the modal can't fire competing requests.
        if (isSubmittingRef.current) {
            return null;
        }

        const currentRemainingTime = getPhoneVerificationCodeRemainingTime(timerStartTime);

        if (currentRemainingTime > 0) {
            return null;
        }

        handlers.setNewStartTime();
        handlers.clearErrorMessage();
        setIsLoading(true);

        try {
            const response = await context.sendPhoneVerificationCode();

            if (response.error) {
                const error = response.error;

                if (error.status === HTTP_STATUS_FOR_TOO_MANY_REQUESTS && !allowResendAfterTooManyRequests) {
                    // Clear timer and localStorage for 429 error status
                    handlers.clearStartTime();
                    setTimer(0);
                }

                setFormError(error);
                if (context.onError) {
                    context.onError(error);
                }
            } else {
                lscache.set(codeSentStorageKey, "1");
            }
        } catch (_err) {
            const error = {message: "Wystąpił błąd podczas wysyłania kodu", status: 500};
            setFormError(error);
            if (context.onError) {
                context.onError(error);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = useCallback(
        async (values: IFormData) => {
            // The auto-submit effect re-fires whenever all four digits are filled; bail out if a submit is
            // already in flight so the verification + onSuccess work can't be triggered twice.
            if (isSubmittingRef.current) {
                return;
            }

            const phoneVerificationCode = `${values.firstDigit}${values.secondDigit}${values.thirdDigit}${values.fourthDigit}`;

            setFormError((prevState) => (prevState ? {...prevState, message: ""} : null));
            isSubmittingRef.current = true;
            setIsSubmitting(true);

            try {
                const response = await context.verifyPhone(phoneVerificationCode);

                if (response.error) {
                    setFormError(response.error);
                    if (context.onError) {
                        context.onError(response.error);
                    }
                    return;
                }

                gtmEventPhoneVerification("submitted");
                if (storageKeyNamespace) {
                    lscache.remove(timerStorageKey);
                    lscache.remove(codeSentStorageKey);
                } else {
                    clearPhoneVerificationTimerLS();
                }

                // Awaited so the CTA stays in its loading state through the work onSuccess starts (typically the
                // Turnstile-protected lead submission). Firing it without awaiting would drop the loader while the
                // captcha/submission is still running, leaving a clickable button on the modal.
                await onSuccess?.();
            } catch (_err) {
                const error = {message: "Wystąpił błąd podczas weryfikacji", status: 500};
                setFormError(error);
                if (context.onError) {
                    context.onError(error);
                }
            } finally {
                isSubmittingRef.current = false;
                setIsSubmitting(false);
            }
        },
        [codeSentStorageKey, context, onSuccess, storageKeyNamespace, timerStorageKey]
    );

    useEffect(() => {
        const subscription = form.watch((values) => {
            const {firstDigit, secondDigit, thirdDigit, fourthDigit} = values;
            const digits = [firstDigit, secondDigit, thirdDigit, fourthDigit];

            if (digits.every((digit) => digit != null)) {
                form.handleSubmit(onSubmit)();
            }
        });

        return () => subscription.unsubscribe();
    }, [form, onSubmit]);

    if (!userPhoneNumber) {
        return null;
    }

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <Text
                variant="headline_3"
                align="left"
                mb={1}
                css={css`
                    ${pr(1)}
                `}
            >
                Zweryfikuj swój <Highlight>numer telefonu</Highlight>
            </Text>

            <Text variant="body_copy_2" mb={4} color="var(--colors-gray-800)">
                Wpisz 4 cyfrowy kod aktywacyjny, który wysłaliśmy na Twój numer telefonu{" "}
                <span css={boldTextStyle}>{phoneNumberFormatNational(userPhoneNumber)}</span>.{" "}
                <span css={changeNumberButtonStyle} onClick={handlers.changeNumberButton}>
                    Zmień numer
                </span>
            </Text>

            <FormProviderRHF {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div css={formStyle}>
                        {digitFields.map((fieldName, index) => (
                            <InputField
                                key={fieldName}
                                ref={(el) => {
                                    inputRefs.current[index] = el;
                                }}
                                name={fieldName}
                                css={inputStyle}
                                inputMode="numeric"
                                autoComplete={index === 0 ? "one-time-code" : "off"}
                                onChange={(e) => {
                                    handlers.inputFocusChange(fieldName, e.target.value);
                                    const numeric = e.target.value.replace(/[^0-9]/g, "");
                                    if (numeric.length > 1) {
                                        e.target.value = numeric.slice(-1);
                                    }
                                }}
                                onKeyDown={(e) => handlers.inputKeyDown(fieldName, e)}
                                onPaste={handlers.inputPaste}
                            />
                        ))}
                    </div>
                    {formError && (
                        <Text variant="info_txt_1" css={errorMessageStyle}>
                            {formError.message}
                        </Text>
                    )}
                    <Button variant="filled_primary" type="submit" fullWidth css={buttonStyle} isLoading={isSubmitting} disabled={isSubmitting}>
                        POTWIERDŹ
                    </Button>
                    {formError?.status !== HTTP_STATUS_FOR_TOO_MANY_REQUESTS || allowResendAfterTooManyRequests ? (
                        <Text variant="info_txt_1" as="div" css={timer > 0 ? timerStyle : sendCodeInfoStyle} onClick={sendNewCode}>
                            {isLoading ? <Loader size="sm" /> : timer > 0 ? `Wyślij kod ponownie za: ${formattedTime}` : "Wyślij kod ponownie"}
                        </Text>
                    ) : (
                        <Text variant="info_txt_1" css={timerStyle}>
                            Nie otrzymałeś kodu? <br />
                            {context.hotlinePhoneNumber && (
                                <>
                                    Skontaktuj się z naszym biurem obsługi:{" "}
                                    <a
                                        css={[
                                            timerStyle,
                                            css`
                                                font-weight: 700;
                                            `
                                        ]}
                                        href={`tel:+${hotlinePhoneFormat}`}
                                    >
                                        {phoneNumberFormat(context.hotlinePhoneNumber)}
                                    </a>
                                </>
                            )}
                        </Text>
                    )}
                </form>
            </FormProviderRHF>
        </>
    );
};

const formStyle = (theme: Theme) => css`
    display: flex;
    flex-direction: row;
    ${gap(1)};
    margin: auto;
    ${mb(4)};
    height: 72px;
    width: fit-content;
    justify-content: center;

    @media (min-width: ${theme.breakpoints.sm}) {
        ${mb(3)};
        height: 112px;
    }
`;

const buttonStyle = css`
    align-items: center;
    ${mb(2)};
`;

const boldTextStyle = css`
    font-weight: 700;
`;

const changeNumberButtonStyle = css`
    text-decoration: underline;
    cursor: pointer;
`;

const inputStyle = (theme: Theme) => css`
    text-align: center;
    border-radius: 8px;
    border: 1px solid var(--colors-gray-600);
    font-size: 30.79px;

    @media (min-width: ${theme.breakpoints.sm}) {
        font-size: 35.92px;
    }

    & div {
        border: none;
    }

    & > div:first-of-type {
        height: 72px;
        width: fit-content;
        max-width: 72px;

        @media (min-width: ${theme.breakpoints.sm}) {
            height: 112px;
            max-width: 112px;
        }

        & > div:first-of-type {
            height: 70px;
            width: fit-content;
            max-width: 72px;

            @media (min-width: ${theme.breakpoints.sm}) {
                height: 110px;
                max-width: 112px;
            }
        }
    }

    & input {
        height: 100%;
        width: 100%;
        text-align: center;
        font-weight: 600;
    }

    & [data-name="field-message"] {
        display: flex;
        justify-content: center;
    }
`;

const timerStyle = css`
    color: var(--colors-gray-800);
    text-align: center;
`;

const sendCodeInfoStyle = css`
    color: var(--colors-gray-800);
    text-align: center;
    cursor: pointer;
    text-decoration: underline;
`;

const errorMessageStyle = css`
    color: var(--colors-danger);
    text-align: center;
    ${mb(2)};
`;
